import { useState } from "react";
import { Check } from "lucide-react";

// The rep application goes to the leads backend: Alan gets a Slack message and a push, and approves it in the rep app.
const APPLY_URL = "https://kdwixjyqhrkjlzkoojfa.supabase.co/functions/v1/apply";

// Every fact here comes from the rep agreement in rareworks-leads site/rep/app.js. No earnings promises.
const details = [
  ["Pay", "20% of everything your client pays, every month, for as long as they stay with us and you stay a rep. A $75 a month client pays you $15 a month."],
  ["When you're paid", "On the 1st, for the month before. We need a W-9 from you before your first payout."],
  ["Schedule", "Your own hours, your own phone and car."],
  ["Commission only", "No hourly pay, salary, mileage, or benefits. You are an independent contractor and handle your own taxes."],
];

const tools = [
  "The rep app, with a lead list you claim from (up to 25 at a time)",
  "Free preview sites to show owners, often already built",
  "Your own contact card to hand out",
];

const field =
  "w-full px-4 py-3 rounded-xl bg-gray-800/50 border border-white/10 text-white placeholder-gray-400 focus:border-teal-500 transition-colors outline-hidden";

const Careers = () => {
  const [form, setForm] = useState({ name: "", cell: "", email: "", town: "", experience: "", knows: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm({ ...form, [k]: e.target.value });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const res = await fetch(APPLY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const out = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(out.error || "Something went wrong.");
      setSent(true);
    } catch (err) {
      // A failed connection is a TypeError with the browser's own wording: never show that to a visitor.
      const known = err instanceof Error && !(err instanceof TypeError);
      setError(`${known ? err.message : "Your application didn't send. Please try again."} You can also call Alan at (601) 441-9656.`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="relative min-h-screen pt-32 pb-24">
      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white mb-4">Careers</h1>
        <p className="text-xl text-gray-400 mb-12">Work with RareWorks in South Mississippi.</p>

        <div className="p-6 sm:p-8 rounded-2xl glass mb-6">
          <h2 className="text-2xl font-semibold text-white mb-1">Sales rep</h2>
          <p className="text-teal-400 font-medium mb-4">Independent contractor</p>
          <p className="text-gray-300 leading-relaxed mb-6">
            Find local businesses that need a website and sign them up with RareWorks. We already build free previews,
            so you often have a real site to show the owner.
          </p>
          <dl className="divide-y divide-white/10 mb-6">
            {details.map(([label, text]) => (
              <div key={label} className="py-3 sm:grid sm:grid-cols-[10rem_1fr] sm:gap-4">
                <dt className="text-white font-medium">{label}</dt>
                <dd className="text-gray-400 leading-relaxed mt-1 sm:mt-0">{text}</dd>
              </div>
            ))}
          </dl>
          <h3 className="text-white font-medium mb-3">What you get</h3>
          <ul className="space-y-2">
            {tools.map((t) => (
              <li key={t} className="flex items-start gap-3 text-gray-300">
                <Check className="w-4 h-4 text-teal-400 shrink-0 mt-1" />
                {t}
              </li>
            ))}
          </ul>
        </div>

        {sent ? (
          <div className="p-6 sm:p-8 rounded-2xl glass border-teal-500/40" role="status">
            <h2 className="text-2xl font-semibold text-white mb-2">Thanks, we have your application</h2>
            <p className="text-gray-400 leading-relaxed">
              Alan reads every one. If it's a fit, you'll get an email to set up your account, then you read and accept
              the rep agreement before you see any leads.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 sm:p-8 rounded-2xl glass">
            <h2 className="text-2xl font-semibold text-white mb-1">Apply</h2>
            <p className="text-gray-400 mb-6">Alan reviews each application. If it's a fit, you'll get an email to set up your account.</p>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <label className="block text-sm font-medium text-gray-300">
                Name
                <input className={`${field} mt-2`} required maxLength={120} autoComplete="name" value={form.name} onChange={set("name")} />
              </label>
              <label className="block text-sm font-medium text-gray-300">
                Cell
                <input className={`${field} mt-2`} required type="tel" maxLength={40} autoComplete="tel" value={form.cell} onChange={set("cell")} />
              </label>
              <label className="block text-sm font-medium text-gray-300">
                Email
                <input className={`${field} mt-2`} required type="email" maxLength={160} autoComplete="email" value={form.email} onChange={set("email")} />
              </label>
              <label className="block text-sm font-medium text-gray-300">
                Town
                <input className={`${field} mt-2`} required maxLength={80} autoComplete="address-level2" value={form.town} onChange={set("town")} />
              </label>
            </div>
            <label className="block text-sm font-medium text-gray-300 mb-4">
              Any sales or customer-facing experience? <span className="font-normal text-gray-400">(optional)</span>
              <textarea className={`${field} mt-2 min-h-24`} maxLength={1000} value={form.experience} onChange={set("experience")} />
            </label>
            <label className="block text-sm font-medium text-gray-300 mb-4">
              Which local business owners do you already know? <span className="font-normal text-gray-400">(optional)</span>
              <textarea className={`${field} mt-2 min-h-24`} maxLength={1000} value={form.knows} onChange={set("knows")} />
            </label>
            <div className="absolute -left-[9999px]" aria-hidden="true">
              <label>
                Website
                <input tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
              </label>
            </div>
            {error && (
              <p role="alert" className="text-rose-400 text-sm mb-4">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={busy}
              className="w-full py-3.5 rounded-xl font-medium text-white bg-linear-to-r/srgb from-teal-500 to-cyan-600 hover:brightness-110 transition-[filter] disabled:opacity-60"
            >
              {busy ? "Sending..." : "Send my application"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
};

export default Careers;
